import { createContext, useCallback, useEffect, useMemo, useState } from "react";
import UserPool from "../apis/UserPool";

const initialState = {
	isLoggedIn: false,
	accessToken: "",
	idToken: "",
	user: "",
};

const AuthContext = createContext(initialState);

export const AuthProvider = ({ children }) => {
	const [auth, setAuth] = useState(initialState);
	const [isInitializing, setIsInitializing] = useState(true);

	const persistSession = useCallback((session, username) => {
		const nextAuth = {
			isLoggedIn: true,
			accessToken: session.getAccessToken().getJwtToken(),
			idToken: session.getIdToken().getJwtToken(),
			user: username,
		};

		localStorage.setItem("isLoggedIn", "true");
		localStorage.setItem("accessToken", nextAuth.accessToken);
		localStorage.setItem("idToken", nextAuth.idToken);
		localStorage.setItem("user", nextAuth.user);
		setAuth(nextAuth);
	}, []);

	const clearSession = useCallback(() => {
		["isLoggedIn", "accessToken", "idToken", "refreshToken", "user"].forEach(
			(key) => localStorage.removeItem(key)
		);
		setAuth(initialState);
	}, []);

	useEffect(() => {
		const currentUser = UserPool.getCurrentUser();

		if (!currentUser) {
			clearSession();
			setIsInitializing(false);
			return;
		}

		currentUser.getSession((error, session) => {
			if (error || !session?.isValid()) {
				clearSession();
			} else {
				persistSession(session, currentUser.getUsername());
			}

			setIsInitializing(false);
		});
	}, [clearSession, persistSession]);

	const completeLogin = useCallback(
		(session, username) => {
			persistSession(session, username);
			setIsInitializing(false);
		},
		[persistSession]
	);

	const signOut = useCallback(() => {
		UserPool.getCurrentUser()?.signOut();
		clearSession();
	}, [clearSession]);

	const value = useMemo(
		() => ({ auth, isInitializing, completeLogin, signOut }),
		[auth, completeLogin, isInitializing, signOut]
	);

	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
